import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { MatTooltip } from '@angular/material/tooltip'
import {
  ElderBasicPaneLayoutComponent,
  ElderDialogService,
  ElderDimensionsInputComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderQuantityInputControlComponent,
  ElderSelectComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { PackageCheckinModeSelectDirective } from '../package-checkin-mode-select.directive'
import { StorageNode } from '../storage-node'
import { StorageNodeAssignmentComponent } from '../storage-node-assignment/storage-node-assignment.component'
import { StorageNodeTypeSelectDirective } from '../storage-node-type-select.directive'
import { StorageNodeService } from '../storage-node.service'

@Component({
  selector: 'app-storage-node-detail',
  templateUrl: './storage-node-detail.component.html',
  styleUrls: ['./storage-node-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    ElderSelectComponent,
    StorageNodeTypeSelectDirective,
    MatSlideToggle,
    ElderQuantityInputControlComponent,
    MatTooltip,
    ElderDimensionsInputComponent,
    PackageCheckinModeSelectDirective,
    MatTabLabel,
    MatTabContent,
    StorageNodeAssignmentComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneTitleComponent,
    ElderPaneSubtitleComponent,
  ],
})
export class StorageNodeDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _node$: BehaviorSubject<StorageNode> = new BehaviorSubject(null)

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  private nodeService = inject(StorageNodeService)

  private toastService = inject(ElderToastService)

  private dialogService = inject(ElderDialogService)

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get node$(): Observable<StorageNode> {
    return this._node$.asObservable()
  }

  public get node(): StorageNode {
    return this._node$.getValue()
  }

  @Input()
  public set node(node: StorageNode) {
    this._node$.next(node)
  }

  public get creationMode(): boolean {
    return !this.node.id
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    this.nodeService.update(this.node).subscribe(
      (node) => {
        this.node = node
        this.toastService.info('messages.persisting_successful')
      },
      (err) => this.toastService.error('messages.saving_failed', err)
    )
  }

  private delete(): void {
    this.dialogService
      .showConfirm({
        title: 'messages.deleting_confirmation_title',
        message: 'storage.confirmDeletionMsg',
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.nodeService.delete(this.node).subscribe(
            () => this.close.emit(),
            (err) => this.toastService.error('messages.deletion_failed', err)
          )
        }
      })
  }

  private emitClose(event: Event): void {
    this.close.emit(event)
  }
}
